import { useEffect, useRef } from 'react'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import {
  CompanyType,
  EstimatedProjectValueBand,
  PlanningApplicationStatus,
  PlanningApplicationType,
  PlanningCategory,
  PlanningClassification,
} from '@leadgen/models/client'
import useCallbackDebounced from '../../hooks/shared/useCallbackDebounced'
import useStableCallback from '../../hooks/shared/useStableCallback'
import { useSelector } from '../../redux/hooks'
import authSelectors from '../../redux/modules/auth/selectors'
import companySelectors from '../../redux/modules/company/selectors'
import filtersForm, {
  planningCategoriesByCompanyTypeMap,
  planningApplicationTypesByCompanyTypeMap,
  planningClassificationsByCompanyTypeMap,
} from './filtersForm'
import { filterPlanningCategoriesForClassifications } from '../../constants/planningTaxonomy'

const defaultPlanningCategories = []
const defaultPlanningClassifications = []
const defaultPlanningApplicationTypes = []
const defaultPlanningApplicationStatuses: PlanningApplicationStatus[] = []
const defaultEstimatedProjectValueBands: EstimatedProjectValueBand[] = []

export const useFiltersForm = ({ onSubmitSuccess }: { onSubmitSuccess?: () => void }) => {
  const companyId = useSelector(authSelectors.getCompanyId)
  const companyType = useSelector(companySelectors.getCompanyType)
  const planningCategories = useSelector(companySelectors.getCompanyPlanningCategories)
  const planningClassifications = useSelector(companySelectors.getCompanyPlanningClassifications)
  const planningApplicationTypes = useSelector(companySelectors.getCompanyPlanningApplicationTypes)
  const planningApplicationStatuses = useSelector(
    companySelectors.getCompanyPlanningApplicationStatuses,
  )
  const estimatedProjectValueBands = useSelector(
    companySelectors.getCompanyEstimatedProjectValueBands,
  )
  const onSubmitSuccessStable = useStableCallback(() => {
    if (onSubmitSuccess) {
      onSubmitSuccess()
    }
  })
  const { reset, setError, watch, getFieldState, setValue, getValues, control, ...rest } = useForm<{
    companyType: CompanyType | null
    planningCategories: PlanningCategory[]
    planningClassifications: PlanningClassification[]
    planningApplicationTypes: PlanningApplicationType[]
    planningApplicationStatuses: PlanningApplicationStatus[]
    estimatedProjectValueBands: EstimatedProjectValueBand[]
  }>({
    mode: 'onTouched',
    defaultValues: {
      companyType,
      planningCategories: planningCategories || defaultPlanningCategories,
      planningClassifications: planningClassifications || defaultPlanningClassifications,
      planningApplicationTypes: planningApplicationTypes || defaultPlanningApplicationTypes,
      planningApplicationStatuses:
        planningApplicationStatuses || defaultPlanningApplicationStatuses,
      estimatedProjectValueBands: estimatedProjectValueBands || defaultEstimatedProjectValueBands,
    },
  })
  const formCompanyType = useWatch({ control, name: 'companyType' })
  const formPlanningCategories = useWatch({ control, name: 'planningCategories' })
  const formPlanningClassifications = useWatch({ control, name: 'planningClassifications' })
  const formPlanningApplicationTypes = useWatch({ control, name: 'planningApplicationTypes' })
  const formPlanningApplicationStatuses = useWatch({ control, name: 'planningApplicationStatuses' })
  const formEstimatedProjectValueBands = useWatch({ control, name: 'estimatedProjectValueBands' })
  useEffect(() => {
    reset(
      {
        companyType: companyType || undefined,
        planningCategories: planningCategories || defaultPlanningCategories,
        planningClassifications: planningClassifications || defaultPlanningClassifications,
        planningApplicationTypes: planningApplicationTypes || defaultPlanningApplicationTypes,
        planningApplicationStatuses:
          planningApplicationStatuses || defaultPlanningApplicationStatuses,
        estimatedProjectValueBands: estimatedProjectValueBands || defaultEstimatedProjectValueBands,
      },
      { keepDirtyValues: true },
    )
  }, [
    companyType,
    estimatedProjectValueBands,
    planningCategories,
    planningApplicationStatuses,
    planningApplicationTypes,
    planningClassifications,
    reset,
  ])
  const companyTypeDirtyRef = useRef(false)
  useEffect(() => {
    const companyTypeFieldState = getFieldState('companyType')
    if (companyTypeFieldState.isDirty) {
      companyTypeDirtyRef.current = true
    }
    if (companyTypeDirtyRef.current) {
      if (!formCompanyType) {
        setValue('planningClassifications', defaultPlanningClassifications)
        setValue('planningApplicationTypes', defaultPlanningApplicationTypes)
        setValue('planningCategories', defaultPlanningCategories)
        return
      }
      const values = getValues()
      const nextPlanningClassifications = values.planningClassifications.filter(
        planningClassification =>
          planningClassificationsByCompanyTypeMap[formCompanyType].includes(planningClassification),
      )
      setValue(
        'planningApplicationTypes',
        values.planningApplicationTypes.filter(planningApplicationType =>
          planningApplicationTypesByCompanyTypeMap[formCompanyType].includes(
            planningApplicationType,
          ),
        ),
      )
      setValue('planningClassifications', nextPlanningClassifications)
      setValue(
        'planningCategories',
        filtersForm
          .normalizePlanningCategories(
            formCompanyType,
            values.planningCategories,
            nextPlanningClassifications,
          )
          .filter(planningCategory =>
            planningCategoriesByCompanyTypeMap[formCompanyType]?.includes(planningCategory),
          ),
      )
    }
  }, [formCompanyType, getFieldState, setValue, getValues])
  useEffect(() => {
    if (!formCompanyType) {
      return
    }
    const values = getValues()
    const nextPlanningCategories = filterPlanningCategoriesForClassifications({
      planningCategories: values.planningCategories.filter(planningCategory =>
        planningCategoriesByCompanyTypeMap[formCompanyType]?.includes(planningCategory),
      ),
      planningClassifications: values.planningClassifications,
    })
    // Filtering only removes invalid categories and preserves order, so a length change means pruning happened.
    if (values.planningCategories.length !== nextPlanningCategories.length) {
      setValue('planningCategories', nextPlanningCategories, { shouldValidate: true })
    }
  }, [formCompanyType, formPlanningClassifications, getValues, setValue])
  const onSubmit: SubmitHandler<{
    companyType: CompanyType | null
    planningCategories: PlanningCategory[]
    planningClassifications: PlanningClassification[]
    planningApplicationTypes: PlanningApplicationType[]
    planningApplicationStatuses: PlanningApplicationStatus[]
    estimatedProjectValueBands: EstimatedProjectValueBand[]
  }> = useCallbackDebounced(
    ({
      companyType,
      planningCategories,
      planningClassifications,
      planningApplicationTypes,
      planningApplicationStatuses,
      estimatedProjectValueBands,
    }: {
      companyType: CompanyType | null
      planningCategories: PlanningCategory[]
      planningClassifications: PlanningClassification[]
      planningApplicationTypes: PlanningApplicationType[]
      planningApplicationStatuses: PlanningApplicationStatus[]
      estimatedProjectValueBands: EstimatedProjectValueBand[]
    }) => {
      if (companyId == null || companyType == null) {
        return
      }
      return filtersForm
        .onSubmit({
          companyId,
          companyType,
          planningCategories: planningCategories,
          planningClassifications,
          planningApplicationTypes,
          planningApplicationStatuses,
          estimatedProjectValueBands,
        })
        .then(() => {
          reset(undefined, { keepValues: true, keepDirty: false, keepDefaultValues: false })
          onSubmitSuccessStable()
        })
        .catch((error: Error) => {
          setError('root', { message: error.message })
        })
    },
    [companyId, onSubmitSuccessStable, reset, setError],
  )
  const getPlanningCategoriesNextValue = useStableCallback(
    (currentValue: Array<string>, event: React.ChangeEvent<HTMLInputElement>) => {
      return filtersForm.getNextPlanningCategories(
        formCompanyType,
        currentValue as PlanningCategory[],
        formPlanningClassifications,
        event.target.value as PlanningCategory,
        event.target.checked,
      )
    },
  )
  return {
    control,
    onSubmit,
    getPlanningCategoriesNextValue,
    reset,
    setError,
    watch,
    formCompanyType,
    formPlanningCategories,
    formPlanningClassifications,
    formPlanningApplicationTypes,
    formPlanningApplicationStatuses,
    formEstimatedProjectValueBands,
    getFieldState,
    setValue,
    getValues,
    ...rest,
  }
}
